/**
 * @fileoverview system-web health polling service — periodically refreshes live
 * System Status telemetry and persists snapshots to Dexie for the dashboard.
 * @module system-web/services/healthPolling
 *
 * Responsibilities: - Poll systemStatusService on an interval, cache snapshots in
 *                      Dexie, and expose subscription state to the dashboard hook
 * Dependencies:     - ./systemStatusService, @firesquirrel/local-db (Dexie), react
 * Architecture:     - Recognized inherently-online monitoring exception: reads live
 *                      operational data yet still flows service → hook → component
 * Notes:            - Dashboards display live telemetry, not normal user data.
 * @localFirst Source of Truth: server (live telemetry), cached in Dexie for display.
 *   Write Flow: poll → write snapshot to Dexie → UI reads via hook. Sync Strategy:
 *   interval polling (no push). Conflict Resolution: latest snapshot wins.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  getScalingTrend,
  getStatusHistory,
  getSystemStatus,
  getTenantSyncHealth,
  type ScalingTrend,
  type ScalingTrendRange,
  type StatusSnapshot,
  type SystemStatusData,
  type TenantSyncHealthRecord,
} from './systemStatusService';

interface PollingSnapshot<T> {
  data: T;
  error: string | null;
  lastUpdated: string | null;
  loading: boolean;
}

export interface SystemStatusPayload {
  status: SystemStatusData | null;
  history: StatusSnapshot[];
}

/**
 * @contract TenantSyncRecord
 * Purpose: One row per tenant in the Sync Health dashboard. Aliases the
 *          backend TenantSyncHealthRecord shape so the page component needs
 *          no changes when the backend adds fields in Phase 2.
 * Fields:  See TenantSyncHealthRecord in systemStatusService.ts.
 * Rules:   healthStatus is derived from tenant platform status in Phase 1;
 *          per-tenant sync metrics arrive in Phase 2.
 * Sync:    n/a (live monitoring read, inherently online).
 */
export type TenantSyncRecord = TenantSyncHealthRecord;

class PollingStore<T> {
  private snapshot: PollingSnapshot<T>;
  private subscribers = new Set<() => void>();
  private pollingClients = new Set<string>();
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;
  private readonly handleVisibilityChange = () => {
    if (!this.pollingClients.size) return;
    void this.fetchNow();
  };

  constructor(
    private readonly fetcher: () => Promise<T>,
    initialData: T,
    private readonly visibleIntervalMs: number,
    private readonly hiddenIntervalMs: number,
  ) {
    this.snapshot = {
      data: initialData,
      error: null,
      lastUpdated: null,
      loading: false,
    };
  }

  getSnapshot = (): PollingSnapshot<T> => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.subscribers.add(listener);
    if (this.subscribers.size === 1 && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (!this.snapshot.lastUpdated && !this.snapshot.loading) {
      void this.fetchNow();
    }
    return () => {
      this.subscribers.delete(listener);
      if (!this.subscribers.size && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
    };
  };

  setPollingEnabled(id: string, enabled: boolean): void {
    if (enabled) {
      this.pollingClients.add(id);
      this.scheduleNext(0);
      return;
    }
    this.pollingClients.delete(id);
    this.ensureTimerState();
  }

  clearPollingEnabled(id: string): void {
    this.pollingClients.delete(id);
    this.ensureTimerState();
  }

  refresh = async (): Promise<void> => {
    await this.fetchNow();
  };

  private emit(): void {
    this.subscribers.forEach(listener => listener());
  }

  private setSnapshot(next: PollingSnapshot<T>): void {
    this.snapshot = next;
    this.emit();
  }

  private ensureTimerState(): void {
    if (!this.pollingClients.size) {
      this.clearTimer();
      return;
    }
    this.scheduleNext();
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private getIntervalMs(): number {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return this.hiddenIntervalMs;
    }
    return this.visibleIntervalMs;
  }

  private scheduleNext(delayMs?: number): void {
    this.clearTimer();
    if (!this.pollingClients.size) return;
    const timeout = delayMs ?? this.getIntervalMs();
    this.timerId = setTimeout(() => {
      this.timerId = null;
      void this.fetchNow();
    }, timeout);
  }

  private async fetchNow(): Promise<void> {
    if (this.inFlight) return this.inFlight;

    const shouldShowLoading = !this.snapshot.lastUpdated;
    if (shouldShowLoading && !this.snapshot.loading) {
      this.setSnapshot({
        ...this.snapshot,
        loading: true,
      });
    }

    this.inFlight = (async () => {
      try {
        const data = await this.fetcher();
        this.setSnapshot({
          data,
          error: null,
          lastUpdated: new Date().toISOString(),
          loading: false,
        });
      } catch (error) {
        this.setSnapshot({
          ...this.snapshot,
          error: error instanceof Error ? error.message : 'Unknown error',
          loading: false,
        });
      } finally {
        this.inFlight = null;
        if (this.pollingClients.size) {
          this.scheduleNext();
        }
      }
    })();

    return this.inFlight;
  }
}

let pollerIdCounter = 0;

function usePollingStore<T>(store: PollingStore<T>, autoRefresh: boolean): PollingSnapshot<T> {
  const pollerIdRef = useRef<string>();
  if (!pollerIdRef.current) {
    pollerIdRef.current = `poller-${++pollerIdCounter}`;
  }

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => {
    const pollerId = pollerIdRef.current!;
    store.setPollingEnabled(pollerId, autoRefresh);
    return () => {
      store.clearPollingEnabled(pollerId);
    };
  }, [autoRefresh, store]);

  return snapshot;
}

async function fetchSystemStatusPayload(): Promise<SystemStatusPayload> {
  // Status is required: swallowing its failure here produced a successful
  // snapshot with a null status, which the dashboard rendered as "Healthy".
  // History is genuinely optional and may degrade to an empty list.
  const [status, history] = await Promise.all([
    getSystemStatus(),
    getStatusHistory().catch(() => ({ snapshots: [] })),
  ]);

  return {
    status,
    history: history.snapshots,
  };
}

async function fetchTenantSyncRecords(): Promise<TenantSyncRecord[]> {
  try {
    const response = await getTenantSyncHealth();
    return response.tenants;
  } catch {
    return [];
  }
}

const systemStatusStore = new PollingStore<SystemStatusPayload>(
  fetchSystemStatusPayload,
  { status: null, history: [] },
  30_000,
  60_000,
);

const tenantSyncStore = new PollingStore<TenantSyncRecord[]>(
  fetchTenantSyncRecords,
  [],
  30_000,
  60_000,
);


export function useSystemStatusPolling(autoRefresh = true) {
  const snapshot = usePollingStore(systemStatusStore, autoRefresh);
  return {
    ...snapshot,
    refresh: systemStatusStore.refresh,
  };
}

export function useTenantSyncPolling(autoRefresh = true) {
  const snapshot = usePollingStore(tenantSyncStore, autoRefresh);
  return {
    ...snapshot,
    refresh: tenantSyncStore.refresh,
  };
}

/**
 * @hook useScalingTrend
 * Purpose:          On-demand Container Apps replica-count trend for the scaling
 *                   analysis panel (7d hourly / 30d / 90d daily).
 * Returns:          { trend, loading, error, range, setRange, reload }.
 * Side Effects:     Fetches via the systemStatusService read on range change /
 *                   reload. The component never calls the API — it drives this
 *                   through setRange/reload only (data-access-pattern compliant).
 * Offline Behavior: Live Azure Monitor query; on failure `trend` stays null and
 *                   `error` is populated. Not persisted (inherently-online read).
 */
export function useScalingTrend(initialRange: ScalingTrendRange = '7d') {
  const [range, setRange] = useState<ScalingTrendRange>(initialRange);
  const [trend, setTrend] = useState<ScalingTrend | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getScalingTrend(range)
      .then(res => {
        if (!cancelled) setTrend(res);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, reloadKey]);

  return { trend, loading, error, range, setRange, reload };
}

